import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDecimal } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ProjectFormData,
  WorkStatus,
  WorkStatusLabels
} from '../../types';
import { getStatusConfig } from '../../components/Projects/ProjectStatusBadge';
import {
  Building2,
  MapPin,
  AlertCircle,
  Search,
  Ruler,
  ChevronRight,
  ChevronLeft,
  User,
} from 'lucide-react';
import projectService from '../../services/projectService';
import LoadingScreen from '../../components/ui/LoadingScreen';
import UnitTypesTab from '../../components/FunctionalUnits/UnitTypesTab';
import UnitLocationsTab from '../../components/FunctionalUnits/UnitLocationsTab';

const ROWS_PER_PAGE = 10;

const FunctionalUnits: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectFormData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [workStatusFilter, setWorkStatusFilter] = useState('all');
  const [creationOrderFilter, setCreationOrderFilter] = useState('newest');
  const [activeTab, setActiveTab] = useState<'projects' | 'types' | 'locations'>('projects');
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const projectsData = await projectService.getAll();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      (project.client || '').toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      `${project.street} ${project.streetNumber} ${project.city}`.toLowerCase().includes(projectSearchTerm.toLowerCase());

    const matchesStatus = workStatusFilter === 'all' || project.projectStatus === workStatusFilter;

    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (creationOrderFilter !== 'all') {
      const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return creationOrderFilter === 'oldest' ? dateA - dateB : dateB - dateA;
    }
    return 0;
  });

  const totalPages = Math.ceil(filteredProjects.length / ROWS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageProjects = filteredProjects.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  useEffect(() => { setPage(1); }, [projectSearchTerm, workStatusFilter, creationOrderFilter]);

  const handleProjectSelect = (projectId: number) => {
    navigate(`/functional-units/project/${projectId}`);
  };

  const handleTabChange = (tab: 'projects' | 'types' | 'locations') => {
    setIsLoading(true);
    setActiveTab(tab);
    // Simular carga
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
            <p className="mt-4 text-sm text-muted-foreground">Cargando...</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'projects':
        return renderProjectsContent();
      case 'types':
        return <UnitTypesTab />;
      case 'locations':
        return <UnitLocationsTab />;
      default:
        return null;
    }
  };

  const renderProjectsContent = () => {
    return (
      <>
        {/* Search and Filters for Projects */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-col items-end gap-4 md:flex-row">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mt-4 md:mt-0">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Estado
                </label>
                <Select value={workStatusFilter} onValueChange={setWorkStatusFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {Object.entries(WorkStatusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">
                  Orden
                </label>
                <Select value={creationOrderFilter} onValueChange={setCreationOrderFilter}>
                  <SelectTrigger className="w-[180px] rounded-lg">
                    <SelectValue placeholder="Sin ordenar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Sin ordenar</SelectItem>
                    <SelectItem value="newest">Más reciente primero</SelectItem>
                    <SelectItem value="oldest">Más antiguo primero</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Search */}
            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">
                Buscar Proyecto
              </label>
              <div className="relative">
                <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar proyectos..."
                  value={projectSearchTerm}
                  onChange={(e) => setProjectSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Projects Table */}
        <div className="overflow-x-auto">
          {filteredProjects.length === 0 ? (
            <div className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium text-foreground">No se encontraron proyectos</h3>
              <p className="text-sm text-muted-foreground">Intenta con otros términos de búsqueda.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-5 py-3 text-left">
                    <span className="text-sm font-medium text-muted-foreground">Proyecto</span>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <span className="text-sm font-medium text-muted-foreground">Ubicación</span>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <span className="text-sm font-medium text-muted-foreground">Superficie</span>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <span className="text-sm font-medium text-muted-foreground">Estado</span>
                  </th>
                  <th className="px-5 py-3 text-center">
                    <span className="text-sm font-medium text-muted-foreground">Acceder</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageProjects.map((project, index) => {
                  const statusConfig = getStatusConfig(project.projectStatus || WorkStatus.EN_CONSTRUCCION);
                  const isLast = index === pageProjects.length - 1;
                  const cellClasses = isLast ? 'px-5 py-3' : 'px-5 py-3 border-b border-border';

                  return (
                    <tr
                      key={project.id}
                      onClick={() => handleProjectSelect(project.id!)}
                      className="transition-colors cursor-pointer hover:bg-muted/30 group"
                    >
                      <td className={cellClasses}>
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 p-2 transition-colors bg-indigo-100 rounded-lg dark:bg-indigo-900/30 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/40">
                            <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium transition-colors text-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                              {project.name}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <User className="w-3 h-3" />
                              {project.client || 'Sin cliente'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className={cellClasses}>
                        <div className="flex items-start gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="flex flex-col">
                            <span className="text-sm text-foreground">
                              {[project.street, project.streetNumber].filter(Boolean).join(' ') || '-'}
                            </span>
                            {project.city && (
                              <span className="text-xs text-muted-foreground">{project.city}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={cellClasses}>
                        <div className="flex items-center gap-1.5">
                          <Ruler className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-foreground">
                            {project.totalM2 ? `${formatDecimal(project.totalM2)} m²` : '-'}
                          </span>
                        </div>
                      </td>
                      <td className={cellClasses}>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
                          <span className={`block h-1.5 w-1.5 rounded-full ${statusConfig.dotColor}`} />
                          {WorkStatusLabels[project.projectStatus || WorkStatus.EN_CONSTRUCCION]}
                        </div>
                      </td>
                      <td className={`${cellClasses} text-center`}>
                        <span className="inline-flex items-center justify-center gap-1 text-xs font-medium transition-colors text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                          Ver unidades
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {filteredProjects.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Mostrando {pageProjects.length} de {filteredProjects.length} proyectos
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  // Mostrar loading solo en la primera carga
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!isLoading && projects.length >= 0) {
      setInitialLoading(false);
    }
  }, [isLoading, projects]);

  if (initialLoading && activeTab === 'projects') {
    return <LoadingScreen message="Cargando proyectos..." />;
  }

  return (
    <div className="w-full h-full p-6 space-y-6">
      {/* Card Container */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Unidades Funcionales</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Gestiona las unidades funcionales de tus desarrollos.
              </p>
              {activeTab === 'projects' && (
                <div className="flex items-center gap-2 px-3 py-2 mt-3 text-sm border rounded-md text-amber-600 bg-amber-50 border-amber-100 w-fit dark:bg-amber-900/10 dark:text-amber-500 dark:border-amber-900/20">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Selecciona un proyecto para crear y administrar sus unidades funcionales.</span>
                </div>
              )}
              {activeTab === 'types' && (
                <div className="flex items-center gap-2 px-3 py-2 mt-3 text-sm border rounded-md text-amber-600 bg-amber-50 border-amber-100 w-fit dark:bg-amber-900/10 dark:text-amber-500 dark:border-amber-900/20">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Define los tipos de unidades (ej: Monoambiente, 2 Ambientes) disponibles para tus unidades funcionales.</span>
                </div>
              )}

              {activeTab === 'locations' && (
                <div className="flex items-center gap-2 px-3 py-2 mt-3 text-sm border rounded-md text-amber-600 bg-amber-50 border-amber-100 w-fit dark:bg-amber-900/10 dark:text-amber-500 dark:border-amber-900/20">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Administra las ubicaciones (ej: Frente, Contrafrente) que pueden tener las unidades funcionales.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <ul className="flex">
            <li>
              <button
                onClick={() => handleTabChange('projects')}
                className={`inline-block py-3 px-6 text-sm font-medium transition-all duration-200 border-b-2 ${activeTab === 'projects'
                  ? 'bg-muted text-foreground border-indigo-600'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
                  }`}
              >
                Proyectos
              </button>
            </li>
            <li>
              <button
                onClick={() => handleTabChange('types')}
                className={`inline-block py-3 px-6 text-sm font-medium transition-all duration-200 border-b-2 ${activeTab === 'types'
                  ? 'bg-muted text-foreground border-indigo-600'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
                  }`}
              >
                Tipos de UF
              </button>
            </li>
            <li>
              <button
                onClick={() => handleTabChange('locations')}
                className={`inline-block py-3 px-6 text-sm font-medium transition-all duration-200 border-b-2 ${activeTab === 'locations'
                  ? 'bg-muted text-foreground border-indigo-600'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/50'
                  }`}
              >
                Ubicaciones de UF
              </button>
            </li>
          </ul>
        </div>

        {/* Tab Content */}
        <div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default FunctionalUnits;